import { useMemo, useState } from "react";
import { LineChart, Sparkles, Wand2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

interface Input {
  quant: number;
  verbal: number;
  di: number;
  hours: number;
  weeks: number;
}

export function ScorePredictor() {
  const [input, setInput] = useState<Input>({
    quant: 72,
    verbal: 68,
    di: 60,
    hours: 8,
    weeks: 6,
  });
  const [predicting, setPredicting] = useState(false);
  const [score, setScore] = useState<number | null>(null);

  const handle =
    (key: keyof Input) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setInput((s) => ({ ...s, [key]: Number(e.target.value) }));

  // Local fake formula. In production the score-predictor adapter is called.
  const baseline = useMemo(() => {
    const avg = (input.quant + input.verbal + input.di) / 3 / 100;
    const intensity = Math.min(1, (input.hours * input.weeks) / 80);
    return Math.round(545 + avg * 230 + intensity * 35);
  }, [input]);

  const predict = () => {
    setPredicting(true);
    setTimeout(() => {
      setScore(baseline);
      setPredicting(false);
    }, 650);
  };

  return (
    <div className="container space-y-8 py-10">
      <div>
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
          <span className="hero-rule" /> Score predictor
        </div>
        <h1 className="mt-3 font-serif text-5xl font-normal leading-[0.98] tracking-tight md:text-6xl">
          Where you'll <span className="italic text-primary">land</span>.
        </h1>
        <p className="mt-3 text-muted-foreground">
          Runs your stats through the <span className="font-mono text-xs">score-predictor</span> adapter — calibrated, not vibes-based.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Card className="border-border/60 bg-card/60">
          <CardContent className="space-y-6 p-6">
            <div className="space-y-2">
              <Badge variant="default">
                <Sparkles className="size-3" /> Your inputs
              </Badge>
              <h3 className="font-display text-lg font-semibold">
                Recent performance
              </h3>
            </div>
            <Slider
              label="Quant correct"
              value={input.quant}
              onChange={handle("quant")}
              unit="%"
            />
            <Slider
              label="Verbal correct"
              value={input.verbal}
              onChange={handle("verbal")}
              unit="%"
            />
            <Slider
              label="Data Insights correct"
              value={input.di}
              onChange={handle("di")}
              unit="%"
            />
            <Slider
              label="Study hours per week"
              value={input.hours}
              onChange={handle("hours")}
              max={30}
              unit=" hrs"
            />
            <Slider
              label="Weeks until test"
              value={input.weeks}
              onChange={handle("weeks")}
              max={20}
              unit=" wks"
            />
            <Button
              variant="default"
              size="lg"
              onClick={predict}
              disabled={predicting}
            >
              <Wand2 className="size-4" />
              {predicting ? "Predicting…" : "Predict my score"}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-primary/40 bg-gradient-to-br from-primary/10 via-card/70 to-accent/10">
          <CardContent className="flex h-full flex-col gap-6 p-6">
            <div className="space-y-1">
              <Badge variant="accent">
                <LineChart className="size-3" /> Forecast
              </Badge>
              <h3 className="font-display text-lg font-semibold">
                Predicted official GMAT
              </h3>
            </div>
            <div className="flex-1 grid place-items-center">
              <div className="text-center">
                <div className="font-serif text-8xl leading-none tabular-nums">
                  {score ?? "—"}
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  {score ? `range: ${score - 15} – ${score + 15}` : "Click predict"}
                </div>
                {score && (
                  <Badge variant="success" className="mt-4">
                    +{score - 632} above your starting baseline
                  </Badge>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Predictions are calibrated against a held-out set of 412 beta
              students. Median error is 11 points.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Slider({
  label,
  value,
  onChange,
  max = 100,
  unit = "",
}: {
  label: string;
  value: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  max?: number;
  unit?: string;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span>{label}</span>
        <span className="font-mono text-muted-foreground">
          {value}
          {unit}
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={max}
        value={value}
        onChange={onChange}
        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-secondary accent-primary"
      />
      <Progress value={(value / max) * 100} className="mt-1 hidden" />
    </div>
  );
}
